import { computed, DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import { Ability } from '@core/abilities-types';
import { CurrentUserSelectors } from '@core/account-data';
import { EmailAiGeneratorService } from '@core/email-ai-logic';
import { FeatureDiscoveryActions } from '@core/feature-discovery-data';
import { I18nService } from '@core/i18n-legacy';
import {
  flexiblePositionStrategyFactory,
  type Step,
  WalkthroughState,
  WalkthroughStepsService,
  WalkthroughTooltipComponent,
} from '@core/walkthrough-legacy';

@Injectable({ providedIn: 'root' })
export class WalkthroughAiWriterService {
  private readonly walkthroughStepsService = inject(WalkthroughStepsService);
  private readonly store = inject(Store);
  private readonly i18n = inject(I18nService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly emailAiGeneratorService = inject(EmailAiGeneratorService);

  private currentUser = this.store.selectSignal(CurrentUserSelectors.data);

  private canUseEmail = computed(() =>
    [Ability.ViewMailbox, Ability.SendEmails].every(val =>
      this.currentUser()?.roleAbilities.includes(val),
    ),
  );

  private steps: Step[] = [
    {
      id: 'walkthrough-step-ai-writer-1',
      position: 1,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'top'],
          heading: this.i18n.translate('app.walkthrough.walkthroughs.ai_writer.step_1.heading'),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.ai_writer.step_1.description',
          ),
          ctaText: this.i18n.translate('app.walkthrough.walkthroughs.ai_writer.step_1.cta_text'),
          hasNewBadge: true,
          showStepCounter: false,
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top' }],
        offset: { offsetX: -12, offsetY: 24 },
      },
    },
  ];

  public start(candidateWithEmail: boolean): void {
    if (
      !this.currentUser()?.featureDiscovery?.aiWriterUsed &&
      this.emailAiGeneratorService.isEmailAiEnabled() &&
      candidateWithEmail &&
      this.canUseEmail()
    ) {
      this.walkthroughStepsService
        .start(this.steps)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(state => {
          if ([WalkthroughState.Finished, WalkthroughState.Canceled].includes(state)) {
            this.store.dispatch(
              FeatureDiscoveryActions.update({ payload: { aiWriterUsed: true } }),
            );
          }
        });
    }
  }
}
